/**
 * Management → Support Inbox (chat.support). Rendered in ManagementDashboard's
 * main area, like TradersView.
 *
 * Summary cards and filter-tab counts come from GET /chat/inbox/counts
 * (`useInboxCounts`, owned by the dashboard so its nav badge shares them),
 * refreshed on every ticket_* frame and on a 15s poll. The list is chatSlice's
 * inbox for the active filter: patched live by ticket_* frames when the
 * outcome is certain, re-fetched when the slice marks it stale, and polled
 * every 15s (RFC) so claimed tickets — which get no live frames unless open —
 * still update.
 *
 * The right panel shows the community room (PublicRoom, reused as-is) or the
 * selected ticket (TicketDetail, which reads it by id — the selection is
 * independent of which filter's list is showing).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/api/client";
import type { ChatInboxCounts, ChatInboxFilter } from "@/api/types";
import { PUBLIC_ROOM_HOLDER, PublicRoom } from "@/components/Chat/PublicRoom";
import { useAppDispatch, useAppSelector } from "@/state/hooks";
import { fetchChatInbox } from "@/state/slices/chatSlice";
import { pushToast } from "@/state/slices/notificationsSlice";
import { PublicRoomRow, TicketList } from "./TicketList";
import { TicketDetail } from "./TicketDetail";
import { useSettledTicketEventSeq } from "./ticketEvents";
import dash from "../ManagementDashboard/ManagementDashboard.module.css";
import styles from "./SupportInbox.module.css";

/** Matches the dashboard's user-list poll (RFC: poll while the view is open). */
export const INBOX_REFRESH_MS = 15_000;
/** Server page size; beyond one page the poll stops (see below). */
const PAGE_SIZE = 50;

const FILTERS: { key: ChatInboxFilter; label: string }[] = [
  { key: "unclaimed", label: "Unclaimed" },
  { key: "mine", label: "Mine" },
  { key: "all", label: "All" },
  { key: "closed", label: "Closed" },
];

const CARDS: { key: keyof ChatInboxCounts; label: string; tone: string }[] = [
  { key: "unclaimed", label: "Unclaimed", tone: styles.toneUnclaimed },
  { key: "mine", label: "Mine (open)", tone: styles.toneMine },
  { key: "all", label: "All tickets", tone: "" },
  { key: "closed", label: "Closed", tone: styles.toneClosed },
];

/**
 * Inbox counts for the dashboard: fetched when enabled, again after a burst
 * of ticket_* frames settles (one fetch per burst, not per frame — see
 * useSettledTicketEventSeq) and every INBOX_REFRESH_MS. A failed fetch keeps
 * the last known counts.
 */
export function useInboxCounts(enabled: boolean): {
  counts: ChatInboxCounts | null;
  refresh: () => void;
} {
  const eventSeq = useSettledTicketEventSeq();
  const [counts, setCounts] = useState<ChatInboxCounts | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    let current = true; // a burst of events: only the newest response lands
    api
      .chatInboxCounts()
      .then((c) => {
        if (current) setCounts(c);
      })
      .catch(() => {});
    return () => {
      current = false;
    };
  }, [enabled, eventSeq, tick]);

  useEffect(() => {
    if (!enabled) return;
    const id = window.setInterval(() => setTick((t) => t + 1), INBOX_REFRESH_MS);
    return () => window.clearInterval(id);
  }, [enabled]);

  const refresh = useCallback(() => setTick((t) => t + 1), []);
  return { counts, refresh };
}

type Selection = { kind: "public" } | { kind: "ticket"; id: string } | null;

export function SupportInbox({
  counts,
  refreshKey = 0,
}: {
  counts: ChatInboxCounts | null;
  /** Bumped by the dashboard's refresh button. */
  refreshKey?: number;
}) {
  const dispatch = useAppDispatch();
  const inbox = useAppSelector((s) => s.chat.inbox);
  const viewerSub = useAppSelector((s) => s.me.sub);
  const socketConnected = useAppSelector((s) => s.chat.socketConnected);
  const publicJoined = useAppSelector((s) => {
    const id = s.chat.publicRoomId;
    return !!id && (s.chat.roomHolders[id] ?? []).includes(PUBLIC_ROOM_HOLDER);
  });
  const publicPreview = useAppSelector((s) => {
    const id = s.chat.publicRoomId;
    const messages = id ? s.chat.rooms[id]?.messages : undefined;
    const last = messages?.[messages.length - 1];
    if (!last) return null;
    return `${last.sender_name || "Someone"}: ${last.message_type === "image" ? "sent a photo" : last.content}`;
  });

  const [filter, setFilter] = useState<ChatInboxFilter>("unclaimed");
  const [selection, setSelection] = useState<Selection>(null);
  // The open ticket left the viewer's scope (e.g. another supporter claimed
  // it): close it quietly rather than leave an error on screen.
  const onTicketGone = useCallback(() => {
    setSelection(null);
    dispatch(
      pushToast({
        kind: "info",
        title: "Ticket no longer available",
        message: "It was taken by another supporter or is no longer available to you.",
      }),
    );
  }, [dispatch]);

  const showing = inbox.filter === filter;
  const rooms = showing ? inbox.rooms : [];
  const loadedCountRef = useRef(0);
  loadedCountRef.current = rooms.length;

  const loadFirstPage = useCallback(
    () => void dispatch(fetchChatInbox({ filter })),
    [dispatch, filter],
  );

  // First page on open, filter change, or the dashboard's refresh button.
  useEffect(() => {
    loadFirstPage();
  }, [loadFirstPage, refreshKey]);

  // A live frame may have added a room this page doesn't hold.
  useEffect(() => {
    if (showing && inbox.stale && inbox.status !== "loading") loadFirstPage();
  }, [showing, inbox.stale, inbox.status, loadFirstPage]);

  // RFC: poll while open, so claimed tickets (no live frames unless open)
  // still update. Skipped once "Load more" went past the first page, so a
  // refresh never collapses a list the supporter has scrolled into.
  useEffect(() => {
    const id = window.setInterval(() => {
      if (loadedCountRef.current <= PAGE_SIZE) loadFirstPage();
    }, INBOX_REFRESH_MS);
    return () => window.clearInterval(id);
  }, [loadFirstPage]);

  return (
    <div className={styles.inbox}>
      <div className={styles.cards}>
        {CARDS.map((c) => (
          <div key={c.key} className={dash.stat} data-testid={`count-${c.key}`}>
            <div className={dash.statLbl}>{c.label}</div>
            <div className={`${dash.statVal} ${c.tone}`}>{counts ? counts[c.key] : "—"}</div>
          </div>
        ))}
      </div>

      <div className={styles.columns}>
        <section className={styles.listCard} aria-label="Tickets">
          <div className={styles.listHead}>
            <h2 className={styles.title}>Tickets</h2>
            <span className={`${styles.live} ${socketConnected ? "" : styles.offline}`}>
              <span className={styles.liveDot} aria-hidden="true" />
              {socketConnected ? "Live" : "Reconnecting…"}
            </span>
          </div>

          <div className={styles.publicWrap}>
            <PublicRoomRow
              joined={publicJoined}
              preview={publicPreview}
              selected={selection?.kind === "public"}
              onSelect={() => setSelection({ kind: "public" })}
            />
          </div>

          <div className={styles.sectionLabel}>Support tickets</div>
          <div className={styles.tabs} role="tablist" aria-label="Ticket filter">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                role="tab"
                aria-selected={filter === f.key}
                className={`${styles.tab} ${filter === f.key ? styles.tabOn : ""}`}
                onClick={() => setFilter(f.key)}
              >
                {f.label}
                {counts ? <span className={styles.tabCount}>{counts[f.key]}</span> : null}
              </button>
            ))}
          </div>

          <div className={styles.scroll}>
            <TicketList
              rooms={rooms}
              viewerSub={viewerSub}
              selectedId={selection?.kind === "ticket" ? selection.id : null}
              onSelect={(id) => setSelection({ kind: "ticket", id })}
              status={showing ? inbox.status : "loading"}
              error={showing ? inbox.error : null}
              hasMore={showing && inbox.nextCursor !== null}
              loadingMore={inbox.loadingMore}
              onLoadMore={() => {
                if (inbox.nextCursor) {
                  void dispatch(fetchChatInbox({ filter, cursor: inbox.nextCursor }));
                }
              }}
              onRetry={loadFirstPage}
            />
          </div>
        </section>

        <section className={styles.detail} aria-label="Conversation">
          {selection?.kind === "public" ? (
            <PublicRoom />
          ) : selection?.kind === "ticket" ? (
            <TicketDetail ticketId={selection.id} onGone={onTicketGone} />
          ) : (
            <div className={styles.placeholder}>Select a ticket to see the conversation.</div>
          )}
        </section>
      </div>
    </div>
  );
}
